import { IntlShape } from 'react-intl';

import { Nullable } from '~/types/Nullable';

import {
  OptimizeState,
  PromptModelPublishStatus,
  PromptModelWorkflowState,
} from '../../../redux/api/types/sylvanus/model';
import { PromptModelWorkflow } from './types';

export const mapBoostStateToDisplay = (
  state: Nullable<OptimizeState>,
  intl: IntlShape,
) => {
  switch (state) {
    case 'done':
      return intl.formatMessage({
        id: 'CustomAiModelDebugUtils.optimizeStateDone',
        defaultMessage: 'Done',
        description:
          'Description for the boost state when it finished successfully',
      });
    case 'error':
      return intl.formatMessage({
        id: 'CustomAiModelDebugUtils.optimizeStateError',
        defaultMessage: 'Error',
        description: 'Description for the boost state when an error ocurred',
      });
    case 'initializing':
      return intl.formatMessage({
        id: 'CustomAiModelDebugUtils.optimizeStateInitializing',
        defaultMessage: 'Initializing',
        description: 'Description for the boost state when it is initializing',
      });
    case 'running':
      return intl.formatMessage({
        id: 'CustomAiModelDebugUtils.optimizeStateRunning',
        defaultMessage: 'Running',
        description: 'Description for the boost state when it is in progress',
      });
    case null:
    default:
      return intl.formatMessage({
        id: 'CustomAiModelDebugUtils.optimizeStateNotApplicable',
        defaultMessage: '—',
        description:
          'Description for the boost state when the model was not boosted',
      });
  }
};

export const mapPublishStatusToDisplay = (
  status: Nullable<PromptModelPublishStatus>,
  intl: IntlShape,
) => {
  switch (status) {
    case 'completed':
      return intl.formatMessage({
        id: 'CustomAiStatusUtils.publishStatusCompleted',
        defaultMessage: 'Completed (Published)',
        description:
          'Description for the publish status when the model publish completed sucessfully',
      });
    case 'failed':
      return intl.formatMessage({
        id: 'CustomAiStatusUtils.publishStatusFailed',
        defaultMessage: 'Error',
        description:
          'Description for the publish status when the model publish had one or more errors',
      });
    case 'in_progress':
      return intl.formatMessage({
        id: 'CustomAiStatusUtils.publishStatusInProgress',
        defaultMessage: 'Publishing in Progress',
        description:
          'Description for the publish status when the model publish is currently in progress',
      });
    case 'resumed':
      return intl.formatMessage({
        id: 'CustomAiStatusUtils.publishStatusResumed',
        defaultMessage: 'Resumed',
        description:
          'Description for the publish status when the model was re-published after being suspended',
      });
    case 'suspended':
      return intl.formatMessage({
        id: 'CustomAiStatusUtils.publishStatusSuspended',
        defaultMessage: 'Suspended',
        description:
          'Description for the publish status when the model was suspended after being published',
      });
    case null:
    default:
      return intl.formatMessage({
        id: 'CustomAiStatusUtils.publishStatusNotPublished',
        defaultMessage: 'Draft (Not Published)',
        description:
          'Description for the publish status when the model was not published',
      });
  }
};

export const mapWorkflowStateToDisplay = (
  state: PromptModelWorkflowState,
  intl: IntlShape,
) => {
  switch (state) {
    case 'aborted':
      return intl.formatMessage({
        id: 'CustomAiDebugModelUtils.abortedStatus',
        defaultMessage: 'Aborted',
        description: 'Label for the model workflow aborted status',
      });
    case 'committing':
      return intl.formatMessage({
        id: 'CustomAiDebugModelUtils.committingStatus',
        defaultMessage: 'Committing',
        description: 'Label for the model workflow committing status',
      });
    case 'completed':
      return intl.formatMessage({
        id: 'CustomAiDebugModelUtils.completedStatus',
        defaultMessage: 'Completed',
        description: 'Label for the model workflow completed status',
      });
    case 'failed':
      return intl.formatMessage({
        id: 'CustomAiDebugModelUtils.failedStatus',
        defaultMessage: 'Failed',
        description: 'Label for the model workflow failed status',
      });
    case 'new':
      return intl.formatMessage({
        id: 'CustomAiDebugModelUtils.newStatus',
        defaultMessage: 'New',
        description: 'Label for the model workflow new status',
      });
    case 'pending':
      return intl.formatMessage({
        id: 'CustomAiDebugModelUtils.pendingStatus',
        defaultMessage: 'Pending',
        description: 'Label for the model workflow pending status',
      });
    case 'predicting':
      return intl.formatMessage({
        id: 'CustomAiDebugModelUtils.predictingStatus',
        defaultMessage: 'Predicting',
        description: 'Label for the model workflow predicting status',
      });
    case 'suspended':
      return intl.formatMessage({
        id: 'CustomAiDebugModelUtils.suspendedStatus',
        defaultMessage: 'Suspended',
        description: 'Label for the model workflow suspended status',
      });
    default:
      return intl.formatMessage({
        id: 'CustomAiDebugModelUtils.defaultStatus',
        defaultMessage: '—',
        description: 'Label for the model workflow default (unknown) status',
      });
  }
};

export const testCanRepublishWorkflowPrediction = ({
  state,
}: PromptModelWorkflow) => {
  return state === 'failed' || state === 'aborted';
};
